import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent } from '@/components/feature/solution/theme';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

interface Step {
  icon: string;
  label: string;
}

interface AgriDigitalIdProps {
  data: {
    eyebrow: string;
    title: string;
    subtitle: string;
    qrTitle: string;
    qrHint: string;
    qrContent: string;
    exampleLabel: string;
    steps: Step[];
    scanTitle: string;
    scanItems: Step[];
  };
}

export default function AgriDigitalId({ data }: AgriDigitalIdProps) {
  const a = accent('green');

  const PUBLIC_ORIGIN = 'https://ltmzqm.amazingsite.co';
  const traceUrl = `${PUBLIC_ORIGIN}/trace/passport?code=EPIN-CQ-20250918-000873`;

  return (
    <section id="digital-id" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />

        <Reveal className="mt-10">
          <div className="mx-auto max-w-[680px] flex flex-col sm:flex-row items-center gap-6 rounded-2xl border border-background-200 bg-background-100 p-6">
            <div className="w-[264px] h-[264px] shrink-0 rounded-xl bg-white border border-background-200 flex items-center justify-center">
              <QRCodeSVG
                value={traceUrl}
                size={232}
                level="L"
                marginSize={2}
                bgColor="#ffffff"
                fgColor="#0f1a14"
              />
            </div>
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2.5">
                <span className={`w-9 h-9 flex items-center justify-center rounded-md ${a.iconBox}`}>
                  <i className="ri-qr-scan-2-line text-lg"></i>
                </span>
                <div className="font-heading font-bold text-foreground-950 text-base">
                  {data.qrTitle}
                </div>
              </div>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{data.qrHint}</p>
              <Link
                to="/solutions/smart-agriculture/trace"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-accent-400"
              >
                <i className="ri-external-link-line"></i>
                在电脑上打开溯源档案
              </Link>
              <p className="mt-2 text-xs text-foreground-400">
                溯源档案实时同步 · 支持真伪核验
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-12">
          {/* 扫码可见 */}
          <Reveal delay={100}>
            <div className="rounded-2xl bg-foreground-950 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500/20 text-accent-300">
                  <i className="ri-qr-scan-2-line text-2xl"></i>
                </span>
                <h3 className="font-heading font-bold text-background-50 text-lg">{data.scanTitle}</h3>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                {data.scanItems.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl bg-background-50/5 border border-background-50/10 px-4 py-3.5 flex items-center gap-3"
                  >
                    <i className={`${item.icon} text-lg text-accent-300`}></i>
                    <span className="text-sm text-foreground-200 whitespace-nowrap">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-2 text-xs text-foreground-400">
                <i className="ri-shield-check-line text-accent-400"></i>
                一物一码 · 全程可追溯 · 数据不可篡改
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}